import { useCreateDialogChip, useCreateRoute } from 'components/create-dialog/CreateDialogContext';
import { draftIdIn } from 'components/create-dialog/useCreateDraft';
import Loader from 'components/Loader';
import { defaultCurrencyOption, useDefaultCurrencyCode } from 'hooks/useDefaultCurrencyCode';
import { useCallback, useEffect, useState } from 'react';
import { useGetContactByIdQuery } from 'store/api/contacts';
import { useGetContractByIdQuery } from 'store/api/contracts';
import { RelationshipTypeCode } from 'types/contacts';
import { ContractForm } from 'types/contracts';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { useClassifiers } from 'utils/useClassifiers';

import { skipToken } from '@reduxjs/toolkit/query';
import { useCustomFields } from 'components/forms/inputs/custom-fields/useCustomFields';
import { EntityType } from 'types/entity';
import { CONTRACT_FORM_CLASSIFIERS, contractDtoToForm } from './contractFormMapping';
import ContractPageForm from './ContractPageForm';

const USED_CLASSIFIERS = [
    'CONTRACT_TYPE',
    'SCOPE_TYPE',
    'DOCUMENT_TEMPLATE_TYPE',
    'INVOICING_INTERVAL_TYPE',
    'TIMECARD_SPECIFICATION_LAYOUT_TYPE',
    'CURRENCY',
    'JOB_ROLE_GROUP_TYPE',
    'CONTRACT_RELATED_KEYWORD_TYPE',
];

const ContactPageAdd = () => {
    const { contactId } = useCreateRoute().params;
    const [initialData, setInitialData] = useState<ContractForm>();

    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: USED_CLASSIFIERS,
    });

    // If accessing /contact/:contactId/contract/new prefill conected client.
    const { data: contact, isLoading: isLoadingContact } = useGetContactByIdQuery(contactId ?? skipToken);
    // The create dialog names the contact only when it arrives filled in as the connected client.
    useCreateDialogChip({
        parent: contact?.relationshipTypeCode === RelationshipTypeCode.CLIENT ? contact.name : null,
    });
    const { initialCustomFieldsObject, isLoading: isLoadingCustomFieldData } = useCustomFields({
        entityType: EntityType.CONTRACT,
    });
    const { code: defaultCurrencyCode, isLoading: isLoadingDefaultCurrency } = useDefaultCurrencyCode();

    // Set initial form data
    const getInitialData = useCallback((): ContractForm | undefined => {
        if (classifiers) {
            return {
                billingNotes: '',
                contractName: '',
                nameExtension: '',
                contractType: null,
                isClientTemplate: false,
                contractTemplate: null,
                documentTemplateType: null,
                tenantCompany: null,
                signedByPersonParty: null,
                companies: [],
                parties:
                    contact && contact.relationshipTypeCode === RelationshipTypeCode.CLIENT
                        ? [
                              {
                                  id: contact.id,
                                  name: contact.name,
                                  partyType: contact.partyType,
                                  relationshipTypeCode: contact.relationshipTypeCode,
                              },
                          ]
                        : [],
                clientReference: '',
                contractNumber: '',
                dmsLink: '',
                dmsNumber: '',
                contractSentAt: null,
                contractSignedAt: null,
                contractTerminatedAt: null,
                financialConditionId: null,
                paymentDeadlineInDays: null,
                prepaymentPaymentDeadlineInDays: null,
                invoicingInfo: '',
                invoicingIntervalType: null,
                discountPct: null,
                interestRatePct: null,
                interestRateB2cPct: null,
                // The firm's default currency; a Concluded by company chosen later leaves it as it is.
                currencyType: defaultCurrencyOption(
                    getValidOptionsFromClassifier<string, string>(classifiers['CURRENCY']),
                    defaultCurrencyCode
                ),
                timecardSpecificationLayoutType: null,
                timecardIncrement: null,
                pricelistId: null,
                pricelist: null,
                pricelistRates: [],
                pricingHash: undefined,
                readOnlyCreatedAt: null,
                beneficialOwners: [],
                beneficialOwnerNotes: '',
                contractRelatedKeywords: [],
                adminFeePercentage: null,
                additionalNotes: '',
                bankAccount: null,
                ...initialCustomFieldsObject,
            };
        }

        return undefined;
    }, [classifiers, contact, initialCustomFieldsObject, defaultCurrencyCode]);

    useEffect(() => {
        if (isLoadingCustomFieldData || isLoadingDefaultCurrency) {
            return;
        }
        setInitialData(getInitialData());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classifiers, contact, isLoadingCustomFieldData, isLoadingDefaultCurrency]);

    if (
        isLoadingClassifiers ||
        isLoadingDefaultCurrency ||
        (contactId && isLoadingContact) ||
        initialData === undefined ||
        isLoadingCustomFieldData
    ) {
        return <Loader />;
    }

    return <ContractPageForm type="add" initialData={initialData} classifiers={classifiers} />;
};

/** The add form loaded with what a draft holds, read as the contract's edit page reads it. */
const ContractDraftAdd = ({ draftId }: { draftId: number }) => {
    const [initialData, setInitialData] = useState<ContractForm>();
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: CONTRACT_FORM_CLASSIFIERS });
    const { data: contract } = useGetContractByIdQuery(String(draftId), { refetchOnMountOrArgChange: true });
    const { initialCustomFieldsObject, isLoading: isLoadingCustomFields } = useCustomFields({
        entityType: EntityType.CONTRACT,
        fields: contract?.customFields,
    });

    // Frozen once loaded, as the edit page's: a refetch must not reset what the user is typing.
    if (!initialData && contract && !isLoadingClassifiers && !isLoadingCustomFields) {
        setInitialData(contractDtoToForm(contract, { classifiers, customFieldValues: initialCustomFieldsObject }));
    }

    return initialData ? (
        <ContractPageForm type="add" initialData={initialData} classifiers={classifiers} />
    ) : (
        <Loader />
    );
};

/** The contract create; its URL with `?draft=<id>` edits that draft instead of a blank contract. */
const ContractPageAddOrDraft = () => {
    const draftId = draftIdIn(useCreateRoute().location.search);
    return draftId === null ? <ContactPageAdd /> : <ContractDraftAdd key={draftId} draftId={draftId} />;
};

export default ContractPageAddOrDraft;
